import { useCallback, useEffect, useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';

import { FAVORITE_DRAG_TYPE } from './constants';

import type { RefCallback } from 'react';
import type { FavoriteService } from '@kc-console/context';
import type { DropSide, FavoriteDragItem } from './constants';

interface UseFavoriteItemDndParams {
  /** hover midpoint 판정 축 — 노출 목록은 가로('x'), 더보기 메뉴는 세로('y') */
  axis: 'x' | 'y';
  /** localOrder 기준 전역 인덱스 */
  index: number;
  onDragEnd: (didDrop: boolean) => void;
  onDragHover: (from: number, to: number, side: DropSide) => void;
  service: Pick<FavoriteService, 'keyword' | 'name'>;
  /** 드래그 출처 영역 — DragLayer가 프리뷰 모양(탑바 칩/메뉴 항목)을 분기하는 데 사용 */
  sourceArea: FavoriteDragItem['sourceArea'];
}

/**
 * 즐겨찾기 아이템 드래그 소스 + hover 훅 (노출 목록 FavoriteItem / 더보기 메뉴 FavoriteMoreItem 공용)
 * @description
 * - drop은 목록 컨테이너(FavoriteList/FavoriteMoreList)에 위임하고, 아이템은 hover로 삽입 위치만 결정한다.
 *   커서가 항목 midpoint(axis 기준)의 앞/뒤인지로 side('before'|'after')를 판정한다.
 * - end 판정은 didDrop()이 아니라 getDropResult()?.dropped === true를 사용한다.
 *   body catch-all(FavoriteBodyDropTarget)이 '취소'를 undefined 반환으로 표현하는데, react-dnd는 drop()이
 *   undefined여도 타깃이 처리한 것으로 보아 didDrop()을 true로 만들기 때문이다.
 * - 네이티브 드래그 이미지를 getEmptyImage()로 숨기고 커스텀 DragLayer(FavoriteDragLayer)가
 *   프리뷰를 렌더한다 (Safari의 transform 조상 스냅샷 버그 우회).
 * - 반환된 connectDnd를 대상 버튼의 callback ref에 합성해 연결한다.
 */
export const useFavoriteItemDnd = ({
  axis,
  index,
  onDragEnd,
  onDragHover,
  service,
  sourceArea,
}: UseFavoriteItemDndParams) => {
  // midpoint 측정용 대상 엘리먼트 ref
  const elementRef = useRef<HTMLButtonElement | null>(null);

  const [{ isDragging }, drag, preview] = useDrag<FavoriteDragItem, void, { isDragging: boolean }>({
    type: FAVORITE_DRAG_TYPE,
    item: { index, keyword: service.keyword, name: service.name, sourceArea },
    collect: monitor => ({ isDragging: monitor.isDragging() }),
    end: (_, monitor) => {
      onDragEnd(monitor.getDropResult<{ dropped?: boolean }>()?.dropped === true);
    },
  });

  const [, drop] = useDrop<FavoriteDragItem>({
    accept: FAVORITE_DRAG_TYPE,
    hover: (dragItem, monitor) => {
      if (!elementRef.current) return;
      if (dragItem.index === index) {
        onDragHover(dragItem.index, index, 'before');
        return;
      }
      // 커서 위치(axis 기준 midpoint 앞/뒤)로 indicator side 결정
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;
      const rect = elementRef.current.getBoundingClientRect();
      const side: DropSide =
        axis === 'x'
          ? clientOffset.x < rect.left + rect.width / 2
            ? 'before'
            : 'after'
          : clientOffset.y < rect.top + rect.height / 2
            ? 'before'
            : 'after';
      onDragHover(dragItem.index, index, side);
    },
  });

  // 네이티브 드래그 이미지를 숨기고 커스텀 DragLayer(FavoriteDragLayer)가 프리뷰를 렌더한다
  // (Safari의 transform 조상 스냅샷 버그 우회)
  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  const connectDnd: RefCallback<HTMLButtonElement> = useCallback(
    element => {
      elementRef.current = element;
      drag(element);
      drop(element);
    },
    [drag, drop],
  );

  return { connectDnd, isDragging };
};
